import { Model, createRecipe } from 'jms-domain'
import { CabinetWidth } from 'jms-domain/src/implementations/common.js'

import type { IdSequence } from '@salsita-ai/platform-project-sdk/domain'

import { configuratorInterface } from '../systems/configurator-interface.js'

const createHoodRecipes = (
  sequence: IdSequence<Model.AnyDomainEntity>,
  wallId: Model.Wall['id'],
  width: number,
  centerX: number
) => {
  if (width > CabinetWidth.W48) {
    return [
      createRecipe({
        id: sequence.nextId('LynxSedonaHoodW36'),
        type: 'LynxSedonaHoodW36',
        x: centerX - CabinetWidth.W36,
        wallId,
      }),
      createRecipe({
        id: sequence.nextId('LynxSedonaHoodW36'),
        type: 'LynxSedonaHoodW36',
        x: centerX,
        wallId,
      }),
    ]
  } else if (width > CabinetWidth.W42) {
    return [
      createRecipe({
        id: sequence.nextId('LynxSedonaHoodW48'),
        type: 'LynxSedonaHoodW48',
        x: centerX - CabinetWidth.W48 / 2,
        wallId,
      }),
    ]
  } else if (width > CabinetWidth.W36) {
    return [
      createRecipe({
        id: sequence.nextId('LynxSedonaHoodW42'),
        type: 'LynxSedonaHoodW42',
        x: centerX - CabinetWidth.W42 / 2,
        wallId,
      }),
    ]
  } else {
    return [
      createRecipe({
        id: sequence.nextId('LynxSedonaHoodW36'),
        type: 'LynxSedonaHoodW36',
        x: centerX - CabinetWidth.W36 / 2,
        wallId,
      }),
    ]
  }
}

/**
 * Assign a hood to a cooking item - place it above it and size it properly.
 */
export const assignHoodToCookingItemTransformer = configuratorInterface.transformerBuilder.when
  .update(['createDomainEntity'])
  .fired.and(ctx => Model.GrillBase.isId(ctx.update.entityId))
  .then.applySideEffect(ctx => {
    const $newRoot = ctx.getNew$Root()

    const entityId = ctx.update.entityId as Model.BottomWallItem['id']
    const $entity = $newRoot.$cabinets.find(c => c.id === entityId)
    if (!$entity) {
      return []
    }

    const $wall = $entity.$wall
    const $existingHood = $wall.$topItems.find(Model.Hood.is)
    if ($existingHood) {
      // Hood already exists, don't create a new one.
      return []
    }

    const sequence = ctx.getIdSequence()
    const cabinetCenterX = $entity.x + $entity.$netWidth / 2

    const newRecipes = createHoodRecipes(sequence, $wall.id, $entity.$netWidth, cabinetCenterX)
    const updates = newRecipes.map(recipe =>
      configuratorInterface.updates.DomainEntityCreation({
        entity: recipe,
        updateTag: 'assignHoodToCookingItem',
        parentId: $wall.id,
      })
    )

    return updates
  })
  .build('Assign a hood to a cooking item')
